// EXCEPCIÓN SEMÁNTICA de color: SOLO se usa desde components/session/**. Decisión del
// usuario — los estados de sesión se pintan como semáforo funcional (azul/amarillo/verde/
// rojo) sin romper el monocromo+dorado del resto de la app. Los tokens viven en palette.js
// (`sessionStatus`, `sessionProximity`) y son mode-aware (claro/oscuro).
import { differenceInCalendarDays, parseISO } from 'date-fns';

export const PROXIMITY = { FAR: 'far', MEDIUM: 'medium', NEAR: 'near' };

const toDate = (value) => {
  if (!value) return null;
  return value instanceof Date ? value : parseISO(value);
};

/**
 * Proximidad de la fecha de la sesión: cerca (≤2 días) / medio (3-7) / lejos (>7).
 *
 * @param  {Date|String}  startDate
 * @returns  {'far'|'medium'|'near'}
 */
export const getProximity = (startDate) => {
  const date = toDate(startDate);
  if (!date) return PROXIMITY.FAR;
  const days = differenceInCalendarDays(date, new Date());
  if (days <= 2) return PROXIMITY.NEAR;
  if (days <= 7) return PROXIMITY.MEDIUM;
  return PROXIMITY.FAR;
};

/**
 * Normaliza un estado de sesión o de asistencia a uno de 4 estados semánticos.
 *
 * @param  {String}       status     SCHEDULED|FINISHED|PRESENT|ABSENT|CANCELLED|*_ABSENT
 * @param  {Date|String}  startDate  para distinguir SCHEDULED futura vs pendiente de reporte
 * @returns  {'scheduled'|'completed'|'absent'|'pending'}
 */
export const normalizeSessionStatus = (status, startDate) => {
  if (status === 'FINISHED' || status === 'PRESENT') return 'completed';
  if (status === 'ABSENT' || status === 'CLIENT_ABSENT' || status === 'TRAINER_ABSENT' || status === 'CANCELLED') {
    return 'absent';
  }
  // SCHEDULED: si la fecha ya pasó es "pendiente de reporte".
  const date = toDate(startDate);
  if (date && date.getTime() < Date.now()) return 'pending';
  return 'scheduled';
};

/**
 * Color sólido para la línea de borde izquierdo (3-4px) de la card de sesión.
 * Programada → AZUL (color de estado, consistente en sesiones uno a uno y grupales);
 * completada → verde; ausente → rojo; pendiente de reporte → ámbar.
 *
 * @param  {Object}       theme
 * @param  {String}       status
 * @param  {Date|String}  startDate
 * @returns  {String}  color hex
 */
export const getSessionLineColor = (theme, status) => {
  const norm = normalizeSessionStatus(status);
  if (norm === 'absent') return theme.palette.sessionStatus.absent.fg;       // rojo
  if (norm === 'completed') return theme.palette.sessionStatus.completed.fg; // verde
  if (norm === 'pending') return theme.palette.sessionStatus.pending.fg;     // ámbar
  return theme.palette.sessionStatus.scheduled.fg;                          // programada → azul
};

/**
 * sx para el chip de estado (tinte + texto legible en ambos modos).
 *
 * @param  {Object}       theme
 * @param  {String}       status
 * @param  {Date|String}  startDate
 * @returns  {Object}  sx
 */
export const getStatusChipSx = (theme, status, startDate) => {
  const norm = normalizeSessionStatus(status, startDate);
  const token = theme.palette.sessionStatus[norm] || theme.palette.sessionStatus.scheduled;
  return {
    backgroundColor: token.bg,
    color: token.fg,
    fontWeight: 700,
    fontSize: '0.68rem',
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    height: 22,
    borderRadius: '6px'
  };
};

/**
 * ¿La sesión ya pasó? (su fin, o su inicio si no hay fin, es anterior a ahora). Se usa para
 * "opacar" cards de días pasados en cualquier calendario.
 *
 * @param  {Date|String}  start
 * @param  {Date|String}  [end]
 * @returns  {Boolean}
 */
export const isPastSession = (start, end) => {
  const ref = toDate(end) || toDate(start);
  if (!ref) return false;
  return ref.getTime() < Date.now();
};

/**
 * sx para atenuar (opacar) una card cuyo día ya pasó. No toca el color del borde lateral
 * (que sigue indicando el estado: programada/completada/ausente).
 *
 * @param  {Boolean}  past
 * @returns  {Object}  sx
 */
export const dimIfPastSx = (past) => (past ? { opacity: 0.55, filter: 'grayscale(0.15)' } : {});

// Claves i18n (ns 'sessions') para cada estado normalizado, con fallback español.
export const STATUS_LABEL_KEYS = {
  scheduled: { key: 'statusScheduled', fallback: 'Programada' },
  completed: { key: 'statusCompleted', fallback: 'Completada' },
  absent: { key: 'statusAbsent', fallback: 'Ausente' },
  pending: { key: 'statusPending', fallback: 'Pendiente reporte' }
};

/**
 * Etiqueta traducida para un estado normalizado.
 *
 * @param  {Function}     t
 * @param  {String}       status
 * @param  {Date|String}  startDate
 * @returns  {String}
 */
export const getStatusLabel = (t, status, startDate) => {
  const norm = normalizeSessionStatus(status, startDate);
  const { key, fallback } = STATUS_LABEL_KEYS[norm];
  return t(key, { ns: 'sessions', defaultValue: fallback });
};

/**
 * Para una ausencia, indica QUIÉN faltó (cliente o entrenador) según el status crudo.
 * Devuelve `null` si el estado no es una ausencia "atribuida".
 *
 * @param  {Function}  t
 * @param  {String}    status  CLIENT_ABSENT | TRAINER_ABSENT | ABSENT | *
 * @returns  {String|null}
 */
export const getAbsenteeLabel = (t, status) => {
  if (status === 'CLIENT_ABSENT') return t('clientAbsent', { ns: 'sessions', defaultValue: 'Cliente ausente' });
  if (status === 'TRAINER_ABSENT') return t('trainerAbsent', { ns: 'sessions', defaultValue: 'Entrenador ausente' });
  return null;
};
